import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ThemeToggle } from './theme.jsx';

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Dubai',
    }).format(d);
  } catch {
    return iso;
  }
}

function Chip({ kind, value }) {
  if (!value) return <span className="muted">—</span>;
  return <span className={`chip ${kind}-${String(value).toLowerCase()}`}>{value}</span>;
}

export default function App() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ open: 0, high_open: 0, total: 0 });
  const [meta, setMeta] = useState(null);
  const [health, setHealth] = useState(null);
  const [status, setStatus] = useState('all');
  const [priority, setPriority] = useState('all');
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 220);
    return () => clearTimeout(t);
  }, [q]);

  const loadHealth = useCallback(async () => {
    try {
      const res = await fetch('/api/health');
      const data = await res.json();
      setHealth(data);
    } catch {
      /* ignore */
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (status && status !== 'all') params.set('status', status);
      if (priority && priority !== 'all') params.set('priority', priority);
      if (qDebounced.trim()) params.set('q', qDebounced.trim());
      const res = await fetch(`/api/tickets?${params}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setTickets(data.tickets || []);
      setStats(data.stats || { open: 0, high_open: 0, total: 0 });
      setMeta(data.meta || null);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [status, priority, qDebounced]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const onRefresh = async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await fetch('/api/sync', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // No API key is expected in demo — still reload cache
        if (data.error === 'no_api_key') {
          await loadTickets();
          await loadHealth();
          return;
        }
        throw new Error(data.message || data.reason || data.error || `HTTP ${res.status}`);
      }
      await loadTickets();
      await loadHealth();
    } catch (err) {
      setError(err.message || String(err));
      await loadTickets();
      await loadHealth();
    } finally {
      setSyncing(false);
    }
  };

  const fixtureMode = health && health.freshdesk_ok === false;

  const countLabel = useMemo(() => {
    if (loading) return 'Loading…';
    return `${tickets.length} shown · ${stats.total} cached`;
  }, [loading, tickets.length, stats.total]);

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <h1>
            <span>Requests</span>
          </h1>
          <div className="sub">MBIO — Marketing · MexAtlantic Freshdesk</div>
        </div>
        <div className="topbar-actions">
          <ThemeToggle />
          <button type="button" className="btn btn-primary" onClick={onRefresh} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Refresh'}
          </button>
        </div>
      </header>

      {fixtureMode && (
        <div className="banner warn">
          Demo mode: no Freshdesk API key configured. Showing fixture marketing tickets.
          Set <code>FRESHDESK_API_KEY</code> to sync live data from the MBIO — Marketing group.
        </div>
      )}

      {error && <div className="banner err">{error}</div>}

      <section className="stats" aria-label="Ticket stats">
        <div className="stat open">
          <div className="label">Open</div>
          <div className="value">{stats.open}</div>
        </div>
        <div className="stat high">
          <div className="label">High + open</div>
          <div className="value">{stats.high_open}</div>
        </div>
        <div className="stat total">
          <div className="label">Total</div>
          <div className="value">{stats.total}</div>
        </div>
      </section>

      <div className="filters">
        <input
          type="search"
          placeholder="Search subject, requester, agent…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search tickets"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status filter">
          <option value="all">All statuses</option>
          <option value="open">Open</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)} aria-label="Priority filter">
          <option value="all">All priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      <div className="meta-line">
        {countLabel}
        {meta?.last_sync_at ? ` · Last sync ${formatWhen(meta.last_sync_at)} GST` : null}
        {health?.build ? ` · build ${health.build}` : null}
      </div>

      <div className="table-wrap">
        <table className="tickets">
          <thead>
            <tr>
              <th>ID</th>
              <th>Subject</th>
              <th>Requester</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Agent</th>
              <th>Group</th>
              <th>Created</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {!loading && tickets.length === 0 && (
              <tr>
                <td colSpan={9} className="empty">
                  No tickets match these filters.
                </td>
              </tr>
            )}
            {tickets.map((t) => (
              <tr key={t.id}>
                <td>
                  <a href={t.url} target="_blank" rel="noreferrer">
                    #{t.id}
                  </a>
                </td>
                <td className="subject">
                  <a href={t.url} target="_blank" rel="noreferrer">
                    {t.subject}
                  </a>
                </td>
                <td>
                  <div>{t.requester || '—'}</div>
                  {t.requester_email ? <div className="muted">{t.requester_email}</div> : null}
                </td>
                <td>
                  <Chip kind="status" value={t.status} />
                </td>
                <td>
                  <Chip kind="priority" value={t.priority} />
                </td>
                <td>{t.agent || <span className="muted">Unassigned</span>}</td>
                <td className="muted">{t.group || '—'}</td>
                <td className="muted">{formatWhen(t.created_at)}</td>
                <td className="muted">{formatWhen(t.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="cards">
        {!loading && tickets.length === 0 && <div className="empty">No tickets match these filters.</div>}
        {tickets.map((t) => (
          <article className="card" key={t.id}>
            <div className="card-top">
              <a href={t.url} target="_blank" rel="noreferrer">
                #{t.id}
              </a>
              <div style={{ display: 'flex', gap: 6 }}>
                <Chip kind="status" value={t.status} />
                <Chip kind="priority" value={t.priority} />
              </div>
            </div>
            <h3>
              <a href={t.url} target="_blank" rel="noreferrer">
                {t.subject}
              </a>
            </h3>
            <div className="card-meta">
              <span>{t.requester || '—'}</span>
              <span>{t.agent || 'Unassigned'}</span>
              <span>{t.group}</span>
              <span>Updated {formatWhen(t.updated_at)}</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
